import { Trans, useTranslation } from 'react-i18next';
import { TransactionStatus } from 'vopay';
import { InfoBanner } from '@/components/UI';

type FundingReverseBannerProps = {
  fundingStatus?: TransactionStatus;
  subTransactionId?: string;
  subTransactionStatus?: TransactionStatus;
  contactName?: string;
  eLinxRequestId?: string;
};

const transComponents = { bold: <span style={{ fontWeight: 600 }} /> };

// Reverse-direction banner for the funding transaction's own details page — the flip side of
// FundingStatusBanner, per the Transaction Confirmation & Details spec's section 2 table.
export default function FundingReverseBanner({
  fundingStatus,
  subTransactionId,
  subTransactionStatus,
  contactName,
  eLinxRequestId,
}: FundingReverseBannerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.Details' });

  if (!subTransactionId) return null;

  // The funding transaction itself being Pending Approval is already covered by the green
  // ApprovalPanel (FundingStatusBanner) shown above this — same rule as the payment side, where
  // "payment pending approval" shows only the green banner and no additional explanation.
  if (fundingStatus === TransactionStatus.PENDING_APPROVAL) return null;

  const hasContactName = !!contactName;
  const values = { transactionId: subTransactionId, contactName, elinxId: eLinxRequestId };

  if (subTransactionStatus === TransactionStatus.PENDING_APPROVAL) {
    if (eLinxRequestId) {
      return (
        <div style={{ marginBottom: 24 }}>
          <InfoBanner colorScheme="neutral">
            {hasContactName ? (
              <Trans
                t={t}
                i18nKey="FundingReverseBanner.waitingApprovalElinx"
                values={values}
                defaults="This funding transaction is waiting for request <bold>{{elinxId}}</bold> to <bold>{{contactName}}</bold> to be approved. The payment link will be ready to use once this request is approved and the funding settles. Payment will start processing after this contact provides their preferred payment method."
                components={transComponents}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="FundingReverseBanner.waitingApprovalElinxNoContact"
                values={values}
                defaults="This funding transaction is waiting for request <bold>{{elinxId}}</bold> to be approved. The payment link will be ready to use once this request is approved and the funding settles. Payment will start processing after this contact provides their preferred payment method."
                components={transComponents}
              />
            )}
          </InfoBanner>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 24 }}>
        <InfoBanner colorScheme="neutral">
          {hasContactName ? (
            <Trans
              t={t}
              i18nKey="FundingReverseBanner.waitingApproval"
              values={values}
              defaults="This funding transaction is waiting for payment ID <bold>{{transactionId}}</bold> to <bold>{{contactName}}</bold> to be approved. Once the payment is approved, this funding will be processed, and the payment will be sent automatically after the funds settle."
              components={transComponents}
            />
          ) : (
            <Trans
              t={t}
              i18nKey="FundingReverseBanner.waitingApprovalNoContact"
              values={values}
              defaults="This funding transaction is waiting for payment ID <bold>{{transactionId}}</bold> to be approved. Once the payment is approved, this funding will be processed, and the payment will be sent automatically after the funds settle."
              components={transComponents}
            />
          )}
        </InfoBanner>
      </div>
    );
  }

  if (fundingStatus === TransactionStatus.FAILED) {
    return (
      <div style={{ marginBottom: 24 }}>
        <InfoBanner colorScheme="error">
          {hasContactName ? (
            <Trans
              t={t}
              i18nKey="FundingReverseBanner.failed"
              values={values}
              defaults="This transaction funds payment ID <bold>{{transactionId}}</bold> to <bold>{{contactName}}</bold>. This funding failed — the payment could not be processed."
              components={transComponents}
            />
          ) : (
            <Trans
              t={t}
              i18nKey="FundingReverseBanner.failedNoContact"
              values={values}
              defaults="This transaction funds payment ID <bold>{{transactionId}}</bold>. This funding failed — the payment could not be processed."
              components={transComponents}
            />
          )}
        </InfoBanner>
      </div>
    );
  }

  const isSettled =
    fundingStatus === TransactionStatus.SETTLED ||
    fundingStatus === TransactionStatus.SUCCESSFUL ||
    fundingStatus === TransactionStatus.COMPLETE;

  if (isSettled) {
    // The funding settling only means the payment can now proceed — it can still fail or
    // succeed on its own, independent reason, so re-check the payment's own status here too
    // instead of assuming "being processed" for the rest of the payment's lifetime.
    const isSubTransactionFailed = subTransactionStatus === TransactionStatus.FAILED;
    const isSubTransactionSettled =
      subTransactionStatus === TransactionStatus.SETTLED ||
      subTransactionStatus === TransactionStatus.SUCCESSFUL ||
      subTransactionStatus === TransactionStatus.COMPLETE;

    if (isSubTransactionFailed) {
      return (
        <div style={{ marginBottom: 24 }}>
          <InfoBanner colorScheme="error">
            {hasContactName ? (
              <Trans
                t={t}
                i18nKey="FundingReverseBanner.settledPaymentFailed"
                values={values}
                defaults="This transaction funds payment ID <bold>{{transactionId}}</bold> to <bold>{{contactName}}</bold>. The funding settled, but the payment failed for a separate reason."
                components={transComponents}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="FundingReverseBanner.settledPaymentFailedNoContact"
                values={values}
                defaults="This transaction funds payment ID <bold>{{transactionId}}</bold>. The funding settled, but the payment failed for a separate reason."
                components={transComponents}
              />
            )}
          </InfoBanner>
        </div>
      );
    }

    if (isSubTransactionSettled) {
      return (
        <div style={{ marginBottom: 24 }}>
          <InfoBanner colorScheme="neutral">
            {hasContactName ? (
              <Trans
                t={t}
                i18nKey="FundingReverseBanner.settledPaymentSucceeded"
                values={values}
                defaults="This transaction funds payment ID <bold>{{transactionId}}</bold> to <bold>{{contactName}}</bold>. The funding settled and the payment was completed."
                components={transComponents}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="FundingReverseBanner.settledPaymentSucceededNoContact"
                values={values}
                defaults="This transaction funds payment ID <bold>{{transactionId}}</bold>. The funding settled and the payment was completed."
                components={transComponents}
              />
            )}
          </InfoBanner>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 24 }}>
        <InfoBanner colorScheme="neutral">
          {hasContactName ? (
            <Trans
              t={t}
              i18nKey="FundingReverseBanner.settled"
              values={values}
              defaults="This transaction funds payment ID <bold>{{transactionId}}</bold> to <bold>{{contactName}}</bold>. The funding settled — the payment is being processed."
              components={transComponents}
            />
          ) : (
            <Trans
              t={t}
              i18nKey="FundingReverseBanner.settledNoContact"
              values={values}
              defaults="This transaction funds payment ID <bold>{{transactionId}}</bold>. The funding settled — the payment is being processed."
              components={transComponents}
            />
          )}
        </InfoBanner>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <InfoBanner colorScheme="neutral">
        {hasContactName ? (
          <Trans
            t={t}
            i18nKey="FundingReverseBanner.pending"
            values={values}
            defaults="This transaction funds payment ID <bold>{{transactionId}}</bold> to <bold>{{contactName}}</bold>. The payment stays in Waiting until this funding settles — if this funding fails, the payment will fail."
            components={transComponents}
          />
        ) : (
          <Trans
            t={t}
            i18nKey="FundingReverseBanner.pendingNoContact"
            values={values}
            defaults="This transaction funds payment ID <bold>{{transactionId}}</bold>. The payment stays in Waiting until this funding settles — if this funding fails, the payment will fail."
            components={transComponents}
          />
        )}
      </InfoBanner>
    </div>
  );
}
